import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { notification } from "antd";
import { StreamApiClient, HlsPlayer, STREAM_CONFIG } from "../../services/streamService";
import { baseUrl } from "../dasboard";
import { PixelModal } from "../../design/retro";

function ViewerPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();

  // Stream state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);

  // Score modal state
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [tableData, setTableData] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);

  // Refs
  const videoRef = useRef(null);
  const apiClientRef = useRef(null);
  const playerRef = useRef(null);
  const viewerIntervalRef = useRef(null);

  // Initialize API client on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const token = localStorage.getItem("token");
    apiClientRef.current = new StreamApiClient(STREAM_CONFIG.apiBaseUrl, token);

    return () => {
      stopPlayback();
      if (viewerIntervalRef.current) {
        clearInterval(viewerIntervalRef.current);
      }
    };
  }, [roomId]);

  // Called from user tap on play button — user gesture is in scope
  const handlePlay = () => {
    startPlayback();
  };

  // Start HLS playback
  const startPlayback = async () => {
    if (!roomId || !videoRef.current) return;

    // Clean up any existing player to prevent duplicate HLS instances
    stopPlayback();

    setIsLoading(true);
    setError(null);

    try {
      // First, fetch the table to get the streamId
      const tableResponse = await fetch(`${baseUrl}/tables/${roomId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!tableResponse.ok) {
        throw new Error("Failed to fetch table data");
      }

      const tableData = await tableResponse.json();
      const streamId = tableData.streamId;

      if (!streamId) {
        throw new Error("No active stream for this table");
      }

      console.log('[Viewer] Using streamId:', streamId);

      // Use streamId for HLS URL
      const hlsUrl = apiClientRef.current.getHlsUrl(streamId);
      playerRef.current = new HlsPlayer(videoRef.current);

      // Load HLS stream (sets up hls.js, waits for manifest)
      await playerRef.current.load(hlsUrl);

      // Explicitly start video playback
      await videoRef.current.play();
      setIsPlaying(true);

      // Start polling viewer count using streamId
      viewerIntervalRef.current = setInterval(async () => {
        const count = await apiClientRef.current.getViewerCount(streamId);
        setViewerCount(count);
      }, 5000);

      // Get initial viewer count
      const initialCount = await apiClientRef.current.getViewerCount(streamId);
      setViewerCount(initialCount);

    } catch (err) {
      console.error("Failed to start playback:", err);
      setError(err.message);
      notification.error({
        message: "Failed to Load Stream",
        description: err.message,
        placement: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Stop playback
  const stopPlayback = () => {
    if (playerRef.current) {
      playerRef.current.stop();
      playerRef.current = null;
    }
    setIsPlaying(false);
  };

  // Toggle mute/unmute
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  // Handle close button
  const handleClose = () => {
    stopPlayback();
    navigate(-1);
  };

  // Fetch table data for score modal
  const fetchTableData = async () => {
    if (!roomId) return;

    setTableLoading(true);
    try {
      const response = await fetch(`${baseUrl}/tables/${roomId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTableData(data);
      } else {
        const errData = await response.json();
        notification.error({
          message: "Failed to load table data",
          description: errData.error || errData.message,
          placement: "top",
        });
      }
    } catch (err) {
      console.error("Failed to fetch table data:", err);
      notification.error({
        message: "Failed to load table data",
        description: err.message,
        placement: "top",
      });
    } finally {
      setTableLoading(false);
    }
  };

  // Open score modal
  const openScoreModal = () => {
    setScoreModalOpen(true);
    fetchTableData();
  };

  // Render player cards in score modal
  const renderPlayerScore = (player) => {
    const pointsClass = player.points >= 0 ? 'positive' : 'negative';
    return (
      <div key={player.id || player.name} className="score-player-card">
        <span className="score-player-name">{player.name || player.username}</span>
        <span className={`score-player-points ${pointsClass}`}>
          {player.points >= 0 ? '+' : ''}{player.points}
        </span>
      </div>
    );
  };

  return (
    <div className="viewer-container">
      {/* Video Player */}
      <video
        ref={videoRef}
        className="viewer-video"
        playsInline
        controls={false}
      />

      {/* Overlay UI */}
      <div className="viewer-overlay">
        {/* Top bar */}
        <div className="viewer-top-bar">
          {/* Live badge */}
          <div className="live-badge-container">
            <div className="live-badge">
              <span className="live-dot"></span>
              LIVE
            </div>
            {isPlaying && (
              <div className="viewer-count">{viewerCount} Viewers</div>
            )}
          </div>

          {/* Logo */}
          <div className="viewer-logo">
            <span className="logo-ball">🎱</span>
          </div>

          {/* Close button */}
          <button className="viewer-close-button" onClick={handleClose}>
            <span>✕</span>
            <span className="close-label">Close</span>
          </button>
        </div>

        {/* Play button — shown before stream starts */}
        {!isPlaying && !isLoading && !error && (
          <div className="viewer-autoplay-blocked" onClick={handlePlay}>
            <div className="autoplay-play-icon">▶</div>
            <span>Tap to play</span>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="viewer-loading">
            <div className="loading-spinner"></div>
            <span>Loading stream...</span>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="viewer-error">
            <span>Stream not available</span>
            <button onClick={handlePlay}>Retry</button>
          </div>
        )}

        {/* Chat/Events area placeholder */}
        <div className="viewer-chat">
          <div className="chat-message">Player A sent 50pts to Player B</div>
          <div className="chat-message">Viewer45: Great shot!</div>
          <div className="chat-message">SarahJ: Who's winning?</div>
          <div className="chat-message">PlayerX sent 100pts to Admin</div>
        </div>

        {/* Side action buttons */}
        <div className="viewer-side-buttons">
          {isPlaying && (
            <button className="side-button" onClick={toggleMute}>
              <span className="side-icon">{isMuted ? '🔇' : '🔊'}</span>
            </button>
          )}
          <button className="side-button" onClick={openScoreModal}>
            <span className="side-icon">💰</span>
          </button>
          <button className="side-button">
            <span className="side-icon">❤️</span>
          </button>
          <button className="side-button">
            <span className="side-icon">↗️</span>
          </button>
        </div>

        {/* Chat input */}
        <div className="viewer-chat-input">
          <input
            type="text"
            placeholder="Say something..."
            className="chat-input"
          />
        </div>
      </div>

      {/* Score Modal */}
      <PixelModal
        open={scoreModalOpen}
        onClose={() => setScoreModalOpen(false)}
        className="score-modal"
        title="Current Scores"
      >
        <div className="score-modal-content">
          {tableLoading ? (
            <div className="score-loading">Loading...</div>
          ) : tableData ? (
            <div className="score-players-list">
              {tableData.tableName && (
                <div className="score-table-name">{tableData.tableName}</div>
              )}
              {tableData.players?.map(renderPlayerScore)}
            </div>
          ) : (
            <div className="score-empty">No data available</div>
          )}

          <button
            type="button"
            className="kenney-create-button kenney-create-button-cancel score-close-btn"
            onClick={() => setScoreModalOpen(false)}
          >
            Close
          </button>
        </div>
      </PixelModal>
    </div>
  );
}

export default ViewerPage;
